import { create } from 'zustand';
import api from '../services/api';
import { User, RegisterData, LoginData, AuthResponse, ApiResponse } from '../types';
import toast from 'react-hot-toast';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  register: (data: RegisterData) => Promise<void>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  login: (data: LoginData) => Promise<void>;
  logout: () => void;
  fetchCurrentUser: () => Promise<void>;
  setUser: (user: User | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,

  register: async (data: RegisterData) => {
    try {
      set({ isLoading: true });
      const response = await api.post<ApiResponse>('/auth/register', data);

      toast.success(response.data.message || 'Регистрация успешна! Проверьте почту.');

      // Store email for verification page
      sessionStorage.setItem('pendingVerificationEmail', data.email);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Не удалось зарегистрироваться';
      toast.error(message);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  verifyEmail: async (email: string, code: string) => {
    try {
      set({ isLoading: true });
      const response = await api.post<ApiResponse<AuthResponse>>('/auth/verify-email', {
        email,
        code,
      });

      const { user, accessToken, refreshToken } = response.data.data!;

      // Store tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      set({ user, isAuthenticated: true });
      sessionStorage.removeItem('pendingVerificationEmail');

      toast.success('Email подтверждён!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Не удалось подтвердить email';
      toast.error(message);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  resendCode: async (email: string) => {
    try {
      set({ isLoading: true });
      const response = await api.post<ApiResponse>('/auth/resend-verification', { email });

      toast.success(response.data.message || 'Код подтверждения отправлен!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Не удалось отправить код';
      toast.error(message);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  login: async (data: LoginData) => {
    try {
      set({ isLoading: true });
      const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', data);

      const { user, accessToken, refreshToken } = response.data.data!;

      // Store tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      set({ user, isAuthenticated: true });

      toast.success('Вход выполнен!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Не удалось войти';
      toast.error(message);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    set({ user: null, isAuthenticated: false });
    toast.success('Вы вышли из системы');
  },

  fetchCurrentUser: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        set({ isAuthenticated: false, user: null });
        return;
      }

      const response = await api.get<ApiResponse<{ user: User }>>('/auth/me');
      set({ user: response.data.data!.user, isAuthenticated: true });
    } catch (error) {
      set({ isAuthenticated: false, user: null });
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  },

  setUser: (user: User | null) => {
    set({ user, isAuthenticated: !!user });
  },
}));
